import React, { memo, useCallback } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { EditEmailModal } from './edit-email-modal';
import { Button, LinkBase, Paragraph, ParagraphProps, Title } from '@restworld/ui-ds';
import { EditPhoneNumberModal } from './phone-number/edit-phone-number-modal';
import tw, { styled } from 'twin.macro';
import { FlexCol } from './header';
import { useAuth } from '@restworld/feature-authentication';
import { DeleteAccountModal } from './delete-account-modal';
import { useSetAtom } from 'jotai';
import { modalOpen as openPhoneNumberModal, setIsEnterPhoneNumber } from './phone-number/states';
import { useTranslation } from 'next-i18next';

export const AccountSettingsTab = memo(() => {
  const onOpenPhoneNumberModal = useSetAtom(openPhoneNumberModal);
  const setEnterPhoneNumberContent = useSetAtom(setIsEnterPhoneNumber);
  const { t } = useTranslation('user-settings');
  const { user } = useAuth();
  const email = user?.email;
  const phoneNumber = user?.phone_number;
  const isPhoneNumberVerified = user?.phone_number_verified;
  const isLeadUserType = user?.type === 'lead';

  const handleOpen = useCallback(() => {
    setEnterPhoneNumberContent(true);
    onOpenPhoneNumberModal(true);
  }, [onOpenPhoneNumberModal, setEnterPhoneNumberContent]);

  return (
    <FlexCol tw="gap-1">
      <SectionTitle>{t('account_tab_section_title')}</SectionTitle>
      <LgColSmRow tw="lg:gap-16 gap-6">
        <FlexCol tw="gap-4 lg:w-[29.813rem] w-full">
          <BodyText color="gray-600">
            {isLeadUserType ? (
              t('account_tab_section_description_text_lead_user')
            ) : (
              <>
                {t('account_tab_section_description_text_1')}{' '}
                <span tw="font-bold">{t('account_tab_section_description_text_2')}</span>{' '}
                {t('account_tab_section_description_text_3')}{' '}
                <span tw="font-bold">{t('account_tab_section_description_text_4')}</span>{' '}
                {t('account_tab_section_description_text_5')}
              </>
            )}
          </BodyText>
          <LinkBase tw="w-full" newTab={false} link={isLeadUserType ? '/candidati' : '/profilo'}>
            <FlexRow tw="items-center gap-1.5">
              <Paragraph standardVariant="body-sm" font="medium" color="primary-800">
                {isLeadUserType ? t('account_tab_section_update_profile') : t('account_tab_section_edit_cv')}
              </Paragraph>
              <ArrowRightIcon tw="text-primary-800 font-medium text-sm w-4 h-4" />
            </FlexRow>
          </LinkBase>
        </FlexCol>
        <FlexCol tw="flex-1 items-start pt-1 gap-6 ">
          <LgColSmRow tw="justify-between w-full">
            <FieldsContainer tw="flex flex-col">
              <BodyText font="medium" color="black">
                {t('email_update_enter_email_step_email_field')}
              </BodyText>
              <BodyText color="gray-600">{email}</BodyText>
            </FieldsContainer>
            <EditEmailModal {...{ email: email }} />
          </LgColSmRow>
          <LgColSmRow tw="lg:justify-between justify-start lg:items-center items-start w-full">
            <FieldsContainer>
              <BodyText font="medium" color="black">
                {t('account_tab_phone_number_field')}
              </BodyText>
              {phoneNumber && <BodyText color="gray-600">{phoneNumber}</BodyText>}
              {!phoneNumber && (
                <BodyText color="red-600">
                  <span tw="underline">{t('account_tab_fill_phone_number_prompt')}</span>
                </BodyText>
              )}
              {phoneNumber && !isPhoneNumberVerified && (
                <Button variant="borderless" twStyle={tw`p-0 focus:ring-0`} onClick={handleOpen}>
                  <span tw="underline">{t('account_tab_edit_phone_number_button_text')}</span>
                </Button>
              )}
            </FieldsContainer>
            <EditPhoneNumberModal />
          </LgColSmRow>
        </FlexCol>
      </LgColSmRow>
      <hr tw="bg-gray-200  h-px border-0 w-full lg:mt-18.25 lg:mb-6 my-6" />
      <FlexCol>
        <SectionTitle>{t('account_tab_delete_account_prompt_text')}</SectionTitle>
        <div tw="lg:w-[38.125rem] w-full mb-4 mt-1">
          <BodyText color="gray-600">{t('account_tab_delete_account_prompt_description')}</BodyText>
        </div>
        <DeleteAccountModal />
      </FlexCol>
    </FlexCol>
  );
});

export const LgColSmRow = tw.div`flex lg:flex-row flex-col`;

export const FlexRow = tw.div`flex flex-row`;

export const FieldsContainer = styled(FlexCol)(() => [tw`gap-1 w-full lg:mb-0 mb-6`]);

export const SectionTitle = ({ children }: { children: React.ReactNode | string }) => {
  return (
    <Title fontWeight="semibold" textColor="black" standardVariant="h5">
      {children}
    </Title>
  );
};

export const BodyText = ({ children, ...rest }: Pick<ParagraphProps, 'children' | 'color' | 'font'>) => {
  return (
    <>
      {' '}
      <div tw="w-full lg:block hidden whitespace-nowrap">
        <Paragraph standardVariant="body-m" {...rest}>
          {children}
        </Paragraph>
      </div>
      <div tw="w-full lg:hidden block whitespace-nowrap">
        <Paragraph standardVariant="body-sm" {...rest}>
          {children}
        </Paragraph>
      </div>
    </>
  );
};
